import React, { useState } from 'react';
import { X, Check, Globe, Clock, DollarSign, AlertTriangle, Sparkles, CheckCircle2, Truck, Route, Pause, Building } from 'lucide-react';

function timeStringToSec(timeStr = '08:00') {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':').map(Number);
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60;
}

function secondsToTimeString(totalSec = 0) {
  const finalH = Math.floor((totalSec / 3600) % 24);
  const finalM = Math.floor((totalSec % 3600) / 60);
  return `${String(finalH).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`;
}

function calcDepartureTimeStr(arrivalStr = '08:00', serviceMins = 10) {
  if (!arrivalStr) return '08:10';
  const arrSec = timeStringToSec(arrivalStr);
  const depSec = arrSec + (parseInt(serviceMins || 10) * 60);
  return secondsToTimeString(depSec);
}

function formatSecToHoursMins(sec = 0) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function calcHaversineDistanceKm(lat1, lon1, lat2, lon2) {
  const p1 = parseFloat(lat1);
  const p2 = parseFloat(lon1);
  const p3 = parseFloat(lat2);
  const p4 = parseFloat(lon2);
  if (isNaN(p1) || isNaN(p2) || isNaN(p3) || isNaN(p4)) return 8.5;
  const R = 6371;
  const dLat = (p3 - p1) * Math.PI / 180;
  const dLon = (p4 - p2) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(p1 * Math.PI / 180) * Math.cos(p3 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c * 1.3;
  return Math.round(d * 10) / 10;
}

function renderStopRealTimeWindows(stop, arrivalTimeFormatted = null) {
  let stopTimeWindows = [];
  if (Array.isArray(stop?.time_windows) && stop.time_windows.length > 0) {
    stopTimeWindows = stop.time_windows.map(tw => ({
      start: tw.start ? tw.start.slice(0, 5) : '08:00',
      end: tw.end ? tw.end.slice(0, 5) : '18:00'
    }));
  } else if (stop?.time_window_start) {
    stopTimeWindows = [{
      start: stop.time_window_start.slice(0, 5),
      end: (stop.time_window_end || '18:00').slice(0, 5)
    }];
  } else {
    stopTimeWindows = [{ start: '08:00', end: '18:00' }];
  }

  const arrSec = arrivalTimeFormatted ? timeStringToSec(arrivalTimeFormatted) : null;
  let isAnyHit = false;
  if (arrSec !== null) {
    isAnyHit = stopTimeWindows.some(tw => {
      const sSec = timeStringToSec(tw.start);
      const eSec = timeStringToSec(tw.end);
      return arrSec >= sSec && arrSec <= eSec;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', justifyContent: 'center' }}>
      {stopTimeWindows.map((tw, i) => {
        let isHit = false;
        if (arrSec !== null) {
          const sSec = timeStringToSec(tw.start);
          const eSec = timeStringToSec(tw.end);
          isHit = arrSec >= sSec && arrSec <= eSec;
        }

        let bg = 'rgba(245, 158, 11, 0.15)';
        let color = '#f59e0b';
        let border = '1px solid rgba(245, 158, 11, 0.3)';

        if (arrSec !== null) {
          if (isHit) {
            bg = 'rgba(34, 197, 94, 0.2)';
            color = '#22c55e';
            border = '1px solid #166534';
          } else if (isAnyHit) {
            bg = '#0f172a';
            color = '#64748b';
            border = '1px solid #334155';
          } else {
            bg = 'rgba(239, 68, 68, 0.25)';
            color = '#ef4444';
            border = '1px solid #991b1b';
          }
        }

        return (
          <span
            key={i}
            style={{
              background: bg,
              color: color,
              border: border,
              padding: '1px 5px',
              borderRadius: '3px',
              fontSize: '0.66rem',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              lineHeight: '1.2'
            }}
          >
            {tw.start} - {tw.end}
          </span>
        );
      })}
    </div>
  );
}

function SingleRouteCard({ isOptimized, job, stops = [], pauses = [], startHub, endHub, truck }) {
  const startHubName = startHub?.street || startHub?.name || 'Dépôt Principal';
  const endHubName = endHub?.street || endHub?.name || startHubName;
  const startTime = job?.estimated_departure ? job.estimated_departure.slice(0,5) : (job?.planned_start_time ? job.planned_start_time.slice(0,5) : '07:30');

  const durationSec = job?.estimated_duration_sec || job?.planned_duration_sec || 0;
  const durationStr = formatSecToHoursMins(durationSec);
  const returnTimeStr = secondsToTimeString(timeStringToSec(startTime) + durationSec);
  const costVal = parseFloat(job?.estimated_cost || job?.planned_cost || 0).toFixed(2);
  const lengthKm = parseFloat(job?.estimated_length_km || job?.planned_length_km || 0);

  const truckInfo = truck || job?.truck || {};
  const rawDate = job?.delivery_date || job?.job_date_str || stops[0]?.delivery_date || null;
  let formattedDate = null;
  if (rawDate) {
    const dStr = String(rawDate).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dStr)) {
      const [yyyy, mm, dd] = dStr.split('-');
      formattedDate = `${dd}/${mm}/${yyyy}`;
    }
  }

  return (
    <div style={{
      background: '#1e293b',
      border: isOptimized ? '1px solid #22c55e' : '1px solid #334155',
      borderRadius: '10px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isOptimized ? '0 0 12px rgba(34, 197, 94, 0.15)' : 'none',
      marginBottom: '16px'
    }}>
      {/* HEADER BANNER */}
      <div style={{
        background: isOptimized ? 'rgba(34, 197, 94, 0.15)' : '#0f172a',
        padding: '10px 14px',
        borderBottom: isOptimized ? '1px solid #166534' : '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Truck size={15} color={isOptimized ? '#22c55e' : '#38bdf8'} />
          <span style={{ fontSize: '0.88rem', fontWeight: '800', color: isOptimized ? '#22c55e' : '#f8fafc' }}>
            {isOptimized ? 'Optimisé' : 'Actuel'} — {job?.display_name || (isOptimized ? 'Tournée' : 'Mission')} {truckInfo.name ? `(${truckInfo.name})` : ''}
          </span>
          {formattedDate && (
            <span style={{
              fontSize: '0.68rem',
              fontWeight: '700',
              color: '#38bdf8',
              background: 'rgba(56, 189, 248, 0.12)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              padding: '1px 6px',
              borderRadius: '4px'
            }}>
              {formattedDate}
            </span>
          )}
          {isOptimized && (job?.trip_index || 0) > 0 && (
            <span
              title={job?.previous_trip_end
                ? `Retour au dépôt à ${String(job.previous_trip_end).slice(0, 5)}${job?.reload_before_mins ? ` + ${job.reload_before_mins} min de rechargement` : ''}`
                : undefined}
              style={{
                fontSize: '0.68rem',
                fontWeight: '700',
                color: job?.is_hub_refill ? '#f59e0b' : '#a78bfa',
                background: job?.is_hub_refill ? 'rgba(245, 158, 11, 0.12)' : 'rgba(167, 139, 250, 0.12)',
                border: job?.is_hub_refill ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid rgba(167, 139, 250, 0.35)',
                padding: '1px 6px',
                borderRadius: '4px'
              }}
            >
              {job?.is_hub_refill ? '🏭 Rechargement' : '🔁 Enchaînement'} · Tour {job?.trip_number || (job.trip_index + 1)}
              {job?.reload_before_mins > 0 ? ` · ${job.reload_before_mins} min dépôt` : ''}
            </span>
          )}
        </div>

        <div style={{ fontSize: '0.78rem', color: isOptimized ? '#22c55e' : '#94a3b8', display: 'flex', gap: '10px', fontWeight: '700', flexShrink: 0 }}>
          <span>{lengthKm} km</span>
          <span>{durationStr}</span>
          <span>{costVal} CHF</span>
        </div>
      </div>

      {/* DÉPART BLOCK */}
      <div style={{ background: '#0f172a', padding: '6px 10px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
        <Building size={13} color="#94a3b8" />
        <span>Départ :</span>
        <span style={{ fontSize: '0.72rem', color: '#ffffff', fontWeight: '700' }}>{startTime}</span>
        <span style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', marginLeft: 'auto' }}>
          {startHubName}
        </span>
      </div>

      {/* TRAJET 1: START HUB TO FIRST STOP */}
      {stops.length > 0 && (() => {
        const firstStop = stops[0];
        const km = firstStop.leg_distance_km !== undefined
          ? firstStop.leg_distance_km
          : calcHaversineDistanceKm(startHub?.latitude, startHub?.longitude, firstStop.latitude, firstStop.longitude);
        let mins = firstStop.leg_drive_mins !== undefined
          ? firstStop.leg_drive_mins
          : Math.max(1, Math.round((km / 40) * 60));
        if (firstStop.leg_drive_mins === undefined && firstStop.estimated_arrival) {
          const startSec = timeStringToSec(startTime);
          const arrSec = timeStringToSec(firstStop.estimated_arrival);
          const firstPauses = (pauses || []).filter(p => p.before_stop_id === firstStop.id || parseInt(p.before_stop_id) === parseInt(firstStop.id));
          const firstPauseSec = firstPauses.reduce((sum, p) => sum + parseInt(p.duration_mins || p.estimated_length_mins || 10) * 60, 0);
          const driveSec = arrSec - firstPauseSec - startSec;
          if (driveSec > 0) {
            mins = Math.max(1, Math.round(driveSec / 60));
          }
        }
        return (
          <div style={{ background: '#0f172a', padding: '4px 10px', borderBottom: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
            <Truck size={13} color="#94a3b8" />
            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
          </div>
        );
      })()}

      {/* STOPS TABLE */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0', maxHeight: '35vh' }}>
        <table className="data-table" style={{ background: 'transparent', border: 'none', width: '100%' }}>
          <tbody>
            {stops.map((stop, idx) => {
              const arrivalTimeFormatted = stop.estimated_arrival ? stop.estimated_arrival.slice(0,5) : '08:00';
              const serviceMins = parseInt(stop.service_time_mins || 10);
              const departureTimeFormatted = calcDepartureTimeStr(arrivalTimeFormatted, serviceMins);
              const pausesForStop = (pauses || []).filter(p => p.before_stop_id === stop.id || parseInt(p.before_stop_id) === parseInt(stop.id));

              return (
                <React.Fragment key={stop.id || idx}>
                  {pausesForStop.map((p, pIdx) => {
                    const dur = p.duration_mins || p.estimated_length_mins || 10;
                    const pStart = p.start || p.estimated_start;
                    const pEnd = p.end || p.estimated_end;
                    const timeWindowStr = pStart && pEnd ? ` (${pStart} - ${pEnd})` : '';
                    return (
                      <tr key={`pause-${p.id || pIdx}`} style={{ background: '#0f172a' }}>
                        <td colSpan={4} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8', fontWeight: '400' }}>
                            <Pause size={13} color="#94a3b8" />
                            <span>Pause : {dur} min{timeWindowStr}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                    {/* COL 1: TIMING STACK */}
                    <td style={{ padding: '6px 6px', textAlign: 'center', width: '56px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#94a3b8' }}>{arrivalTimeFormatted}</span>
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{serviceMins}m</span>
                        <span style={{ fontSize: '0.7rem', fontWeight: '500', color: '#64748b' }}>{departureTimeFormatted}</span>
                      </div>
                    </td>

                    {/* COL 2: REAL TIME WINDOWS COLUMN */}
                    <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle', width: '90px' }}>
                      {renderStopRealTimeWindows(stop, arrivalTimeFormatted)}
                    </td>

                    {/* COL 3: CLIENT & ADDRESS WITH STATUS DOT (VERTICALLY CENTERED LEFT CIRCLE & ALIGNED STOP CODE, NAME, ADDRESS) */}
                    <td style={{ padding: '6px 6px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            background: isOptimized ? '#22c55e' : '#38bdf8',
                            color: '#0f172a',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '800',
                            fontSize: '0.7rem',
                            flexShrink: 0
                          }}
                        >
                          {idx + 1}
                        </span>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                          {/* 1. Stop Code */}
                          <span
                            style={{
                              fontSize: '0.68rem',
                              color: '#94a3b8',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={stop.stop_number || `STOP-${stop.id || (idx + 1)}`}
                          >
                            {stop.stop_number || `STOP-${stop.id || (idx + 1)}`}
                          </span>

                          {/* 2. Client Name */}
                          <strong
                            style={{
                              color: '#ffffff',
                              fontSize: '0.76rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '160px',
                              display: 'inline-block'
                            }}
                            title={stop.client_name}
                          >
                            {stop.client_name}
                          </strong>

                          {/* 3. Address */}
                          <div
                            style={{
                              fontSize: '0.68rem',
                              color: '#94a3b8',
                              marginTop: '1px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '180px'
                            }}
                            title={`${stop.address || ''}, ${stop.city || ''}`}
                          >
                            {stop.address}, {stop.city}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* COL 4: WEIGHT & VOLUME */}
                    <td style={{ padding: '6px 6px', textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '0.72rem', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                          {stop.type === 'pickup' ? '+' : '-'}{Math.round(stop.weight_kg || 0)} kg
                        </span>
                        <span style={{ fontSize: '0.72rem', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                          {stop.type === 'pickup' ? '+' : '-'}{stop.volume_m3 || 0.5} m³
                        </span>
                      </div>
                    </td>
                  </tr>

                  {/* INTER-STOP TRAJET ROW */}
                  {idx < stops.length - 1 && (() => {
                    const nextStop = stops[idx + 1];
                    const km = nextStop.leg_distance_km !== undefined
                      ? nextStop.leg_distance_km
                      : calcHaversineDistanceKm(stop.latitude, stop.longitude, nextStop.latitude, nextStop.longitude);
                    let mins = nextStop.leg_drive_mins !== undefined
                      ? nextStop.leg_drive_mins
                      : Math.max(1, Math.round((km / 40) * 60));
                    if (nextStop.leg_drive_mins === undefined && stop.estimated_arrival && nextStop.estimated_arrival) {
                      const currArrSec = timeStringToSec(stop.estimated_arrival);
                      const nextArrSec = timeStringToSec(nextStop.estimated_arrival);
                      const serviceSec = (parseInt(stop.service_time_mins || 10)) * 60;
                      const nextPauses = (pauses || []).filter(p => p.before_stop_id === nextStop.id || parseInt(p.before_stop_id) === parseInt(nextStop.id));
                      const nextPauseSec = nextPauses.reduce((sum, p) => sum + parseInt(p.duration_mins || p.estimated_length_mins || 10) * 60, 0);
                      const driveSec = nextArrSec - nextPauseSec - (currArrSec + serviceSec);
                      if (driveSec > 0) {
                        mins = Math.max(1, Math.round(driveSec / 60));
                      }
                    }

                    return (
                      <tr key={`leg-${stop.id || idx}`} style={{ background: '#0f172a' }}>
                        <td colSpan={4} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <Truck size={13} color="#94a3b8" />
                            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* TRAJET END: LAST STOP TO END HUB */}
      {stops.length > 0 && (() => {
        const lastStop = stops[stops.length - 1];
        const km = job?.return_leg_distance_km !== undefined
          ? job.return_leg_distance_km
          : calcHaversineDistanceKm(lastStop.latitude, lastStop.longitude, endHub?.latitude, endHub?.longitude);
        let mins = job?.return_leg_drive_mins !== undefined
          ? job.return_leg_drive_mins
          : Math.max(1, Math.round((km / 40) * 60));
        if (job?.return_leg_drive_mins === undefined && lastStop.estimated_arrival && returnTimeStr) {
          const lastArrSec = timeStringToSec(lastStop.estimated_arrival);
          const serviceSec = (parseInt(lastStop.service_time_mins || 10)) * 60;
          const retSec = timeStringToSec(returnTimeStr);
          const driveSec = retSec - (lastArrSec + serviceSec);
          if (driveSec > 0) {
            mins = Math.max(1, Math.round(driveSec / 60));
          }
        }
        return (
          <div style={{ background: '#0f172a', padding: '4px 10px', borderTop: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
            <Truck size={13} color="#94a3b8" />
            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
          </div>
        );
      })()}

      {/* RETOUR FOOTER BLOCK */}
      <div style={{ background: '#0f172a', padding: '6px 10px', borderTop: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
        <Building size={13} color="#94a3b8" />
        <span>Retour :</span>
        <span style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: '700' }}>{returnTimeStr}</span>
        <span style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', marginLeft: 'auto' }}>
          {endHubName}
        </span>
      </div>
    </div>
  );
}

export default function CrossOptimizationModal({
  isOpen,
  onClose,
  previewData,
  onApplyCrossOptimization
}) {
  const [activeTab, setActiveTab] = useState('routes');
  const [isApplying, setIsApplying] = useState(false);

  if (!isOpen || !previewData) return null;

  const {
    inputSummary = {},
    outputSummary = {},
    outputRoutes = [],
    unassignedStops = [],
    savings = {}
  } = previewData;

  const currentJobs = inputSummary.currentJobs || [];
  const hasAddedBucketStops = (inputSummary.unassignedBucketStopsCount || 0) > 0 || currentJobs.length === 0;
  const newlyIntegratedStops = Math.max(
    0,
    (outputSummary.outputStopsCount || 0) - (inputSummary.currentAssignedStopsCount ?? currentJobs.reduce((acc, j) => acc + (j.stops?.length || 0), 0))
  );

  const handleApply = async () => {
    setIsApplying(true);
    if (onApplyCrossOptimization) {
      const unassignedIds = unassignedStops.map(s => s.id);
      await onApplyCrossOptimization(inputSummary.inputJobIds || [], outputRoutes, unassignedIds);
    }
    setIsApplying(false);
    onClose();
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="overlay-card" style={{ width: '1000px', maxWidth: '96vw', padding: '0', overflow: 'hidden', background: '#0f172a', border: '1px solid #334155', borderRadius: '12px' }}>
        
        {/* MODAL HEADER */}
        <div style={{ background: '#1e293b', padding: '16px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} color="#38bdf8" />
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#f8fafc' }}>
                Résultat de l'Optimisation Croisée Multi-Missions
              </h3>
            </div>
          </div>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* SAVINGS SUMMARY BADGES */}
        <div style={{ background: 'rgba(56, 189, 248, 0.1)', padding: '12px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Route size={18} color="#38bdf8" />
            <div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tournées Générées</div>
              <div style={{ fontSize: '1rem', fontWeight: '800', color: '#ffffff' }}>
                {outputSummary.outputJobsCount || 0} tournée(s) ({outputSummary.outputStopsCount || 0} stops)
                {hasAddedBucketStops && newlyIntegratedStops > 0 && (
                  <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#22c55e', fontWeight: '700' }}>
                    (+{newlyIntegratedStops} intégrés)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Globe size={18} color="#38bdf8" />
            <div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {hasAddedBucketStops ? 'Distance Totale' : 'Distance'}
              </div>
              {hasAddedBucketStops ? (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: '#38bdf8' }}>
                  {outputSummary.totalKm || 0} km
                </div>
              ) : (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: savings.km > 0 ? '#22c55e' : savings.km === 0 ? '#ffffff' : '#ef4444' }}>
                  {savings.km > 0 ? `-${savings.km} km` : savings.km === 0 ? `0 km` : `+${Math.abs(savings.km)} km`}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={18} color="#f59e0b" />
            <div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {hasAddedBucketStops ? 'Temps Total' : 'Temps'}
              </div>
              {hasAddedBucketStops ? (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: '#f59e0b' }}>
                  {formatSecToHoursMins(outputSummary.totalDurationSec || 0)}
                </div>
              ) : (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: savings.durationSec > 0 ? '#22c55e' : savings.durationSec === 0 ? '#ffffff' : '#ef4444' }}>
                  {savings.durationSec > 0 ? `-${formatSecToHoursMins(savings.durationSec)}` : savings.durationSec === 0 ? `0 min` : `+${formatSecToHoursMins(Math.abs(savings.durationSec))}`}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DollarSign size={18} color="#22c55e" />
            <div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {hasAddedBucketStops ? 'Coût Total' : 'Coûts'}
              </div>
              {hasAddedBucketStops ? (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: '#22c55e' }}>
                  {Number(outputSummary.totalCost || 0).toFixed(2)} CHF
                </div>
              ) : (
                <div style={{ fontSize: '1rem', fontWeight: '800', color: savings.cost > 0 ? '#22c55e' : savings.cost === 0 ? '#ffffff' : '#ef4444' }}>
                  {savings.cost > 0 ? `-${savings.cost.toFixed(2)} CHF` : savings.cost === 0 ? `0.00 CHF` : `+${Math.abs(savings.cost).toFixed(2)} CHF`}
                </div>
              )}
            </div>
          </div>

          {unassignedStops.length > 0 && (
            <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', padding: '4px 12px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertTriangle size={16} color="#ef4444" />
              <span style={{ fontSize: '0.8rem', color: '#fca5a5', fontWeight: '700' }}>
                {unassignedStops.length} stop(s) non inséré(s)
              </span>
            </div>
          )}
        </div>

        {/* NAVIGATION SUB-TABS */}
        <div style={{ display: 'flex', background: '#1e293b', padding: '0 24px', borderBottom: '1px solid #334155' }}>
          <button
            className={`db-sub-tab ${activeTab === 'routes' ? 'active' : ''}`}
            style={{ padding: '10px 16px', fontSize: '0.82rem', borderBottom: activeTab === 'routes' ? '2px solid #38bdf8' : 'none' }}
            onClick={() => setActiveTab('routes')}
          >
            Comparaison des Flottes (Actuel vs Optimisé)
          </button>

          <button
            className={`db-sub-tab ${activeTab === 'dropped' ? 'active' : ''}`}
            style={{ padding: '10px 16px', fontSize: '0.82rem', color: unassignedStops.length > 0 ? '#ef4444' : '#94a3b8', borderBottom: activeTab === 'dropped' ? '2px solid #ef4444' : 'none' }}
            onClick={() => setActiveTab('dropped')}
          >
            Stops non insérés ({unassignedStops.length})
          </button>
        </div>

        {/* MODAL CONTENT BODY */}
        <div style={{ padding: '20px 24px', maxHeight: '58vh', overflowY: 'auto' }}>
          
          {/* TAB 1: TWO COLUMNS (ACTUEL VS OPTIMISÉ) */}
          {activeTab === 'routes' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              
              {/* LEFT COLUMN: ACTUAL INPUT JOBS */}
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.88rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Parcours Actuels ({currentJobs.length} mission(s))
                </h4>
                {currentJobs.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '0.8rem', background: '#1e293b', borderRadius: '8px' }}>
                    Aucune mission pré-existante (Optimisation directe de livraisons à planifier)
                  </div>
                ) : (
                  currentJobs.map((j, idx) => (
                    <SingleRouteCard
                      key={j.id || idx}
                      isOptimized={false}
                      job={j}
                      stops={j.stops || []}
                      pauses={j.pauses || []}
                      startHub={j.startHub}
                      endHub={j.endHub}
                      truck={j.truck}
                    />
                  ))
                )}
              </div>

              {/* RIGHT COLUMN: OPTIMIZED OUTPUT ROUTES */}
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.88rem', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Parcours Optimisés VROOM ({outputRoutes.length} tournée(s))
                </h4>
                {outputRoutes.map((route, idx) => (
                  <SingleRouteCard
                    key={idx}
                    isOptimized={true}
                    job={route}
                    stops={route.stops || []}
                    pauses={route.pauses || []}
                    startHub={route.startHub}
                    endHub={route.endHub}
                    truck={route.truck}
                  />
                ))}
              </div>

            </div>
          )}

          {/* TAB 2: DROPPED STOPS */}
          {activeTab === 'dropped' && (
            <div>
              {unassignedStops.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                  <CheckCircle2 size={36} color="#22c55e" style={{ marginBottom: '8px' }} />
                  <h4>Tous les stops sélectionnés ont été distribués avec succès !</h4>
                  <p style={{ fontSize: '0.8rem' }}>Aucun stop n'a été laissé de côté.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', padding: '10px 14px', borderRadius: '6px', fontSize: '0.8rem', color: '#fca5a5' }}>
                    <strong>Attention :</strong> Ces stops n'ont pas pu être insérés en raison des contraintes d'horaires ou de capacités des véhicules sélectionnés.
                  </div>

                  {unassignedStops.map(s => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #ef4444', padding: '12px 16px', borderRadius: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#f8fafc' }}>{s.client_name} (Stop #{s.id})</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.address}, {s.city}</div>
                        <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '4px', fontWeight: '600' }}>
                          ⚠️ Raison: {s.reason || 'Créneau horaire non respecté ou véhicules au maximum de leur capacité'}
                        </div>
                      </div>

                      <div style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700' }}>
                        Non inséré
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* MODAL FOOTER ACTIONS */}
        <div style={{ background: '#1e293b', padding: '14px 24px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            className="btn-action"
            style={{ padding: '8px 16px', fontSize: '0.82rem', background: 'transparent', color: '#94a3b8', borderColor: '#475569' }}
            onClick={onClose}
            disabled={isApplying}
          >
            Annuler
          </button>

          <button
            className="btn-action"
            style={{ padding: '8px 20px', fontSize: '0.82rem', background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={handleApply}
            disabled={isApplying}
          >
            <Check size={16} />
            <span>{isApplying ? 'Application en cours...' : "Appliquer l'optimisation croisée"}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
